import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dfdClass, DfdClass } from '../utils/dfdRules';
import i18n from '../../../i18n';

interface Props {
  nodes: any[];
  edges: any[];
  /** 그림 영역 높이(px) */
  height?: number;
  className?: string;
}

/** DFD 분류별 색 — 캔버스와 같은 감각으로 데이터/처리/제어/실행을 구분한다 */
const COLOR: Record<DfdClass, { box: string; text: string }> = {
  data: { box: '#dbeafe', text: '#1d4ed8' },      // 데이터 — 파랑
  process: { box: '#dcfce7', text: '#15803d' },   // 처리 — 초록
  control: { box: '#f3e8ff', text: '#7e22ce' },   // 제어(분기/합류) — 보라
  exec: { box: '#fef3c7', text: '#b45309' },      // 실행(SQL/셸/워크플로) — 주황
  ignore: { box: '#f1f5f9', text: '#64748b' },
};

const NODE_W = 108;
const NODE_H = 34;
/** 캔버스 좌표를 그대로 쓰면 노드 사이가 너무 벌어진다 — 미리보기에서는 간격을 좁혀 컴팩트하게 그린다 */
const SQUEEZE_X = 0.58;
const SQUEEZE_Y = 0.7;

/**
 * 마법사 하단 워크플로 미리보기 — buildPreview 가 만든 노드/엣지를 축소해 그린다.
 * 캔버스와 같은 좌표를 쓰므로 옵션(분할 수·검증 등)을 바꾸면 그림이 그대로 따라 바뀐다.
 */
const FlowPreview: React.FC<Props> = ({ nodes, edges, height = 170, className = '' }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth));
    ro.observe(el);
    setBoxW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    if (!nodes.length) return null;
    const xs = nodes.map((n) => n.position.x);
    const ys = nodes.map((n) => n.position.y);
    const minX = Math.min(...xs), minY = Math.min(...ys);
    const maxX = (Math.max(...xs) - minX) * SQUEEZE_X + NODE_W;
    const maxY = (Math.max(...ys) - minY) * SQUEEZE_Y + NODE_H;
    return { minX, minY, w: maxX + 20, h: maxY + 20 };
  }, [nodes]);

  if (!layout) {
    return (
      <div ref={boxRef} className={`flex items-center justify-center text-[0.6875rem] text-gray-400 ${className}`} style={{ height }}>
        {i18n.t('wizard:preview.empty')}
      </div>
    );
  }

  const pos = (n: any) => ({
    x: (n.position.x - layout.minX) * SQUEEZE_X + 10,
    y: (n.position.y - layout.minY) * SQUEEZE_Y + 10,
  });
  const byId: Record<string, any> = Object.fromEntries(nodes.map((n) => [n.id, n]));
  // 가로·세로 모두에 맞춰 줄인다(확대는 하지 않음). 다만 글씨를 못 읽을 만큼은 줄이지 않고 그때는 스크롤한다.
  const scale = Math.max(0.45, Math.min(1, (height - 12) / layout.h, boxW ? (boxW - 12) / layout.w : 1));

  return (
    <div ref={boxRef} className={`overflow-auto ${className}`} style={{ height }}>
      <svg width={layout.w * scale} height={layout.h * scale} viewBox={`0 0 ${layout.w} ${layout.h}`} style={{ maxWidth: 'none' }}
        preserveAspectRatio="xMinYMin meet" className="block">
        <defs>
          <marker id="wzArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 z" fill="#94a3b8" />
          </marker>
        </defs>
        {edges.map((e) => {
          const s = byId[e.source], t = byId[e.target];
          if (!s || !t) return null;
          const a = pos(s), b = pos(t);
          const x1 = a.x + NODE_W, y1 = a.y + NODE_H / 2;
          const x2 = b.x, y2 = b.y + NODE_H / 2;
          const mid = (x1 + x2) / 2;
          return (
            <path key={e.id} d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
              stroke="#94a3b8" strokeWidth={1.2} fill="none" markerEnd="url(#wzArrow)" />
          );
        })}
        {nodes.map((n) => {
          const p = pos(n);
          const c = COLOR[dfdClass(n.type)];
          const label = String(n.data?.label || n.type);
          return (
            <g key={n.id}>
              <rect x={p.x} y={p.y} width={NODE_W} height={NODE_H} rx={6} fill={c.box} stroke={c.text} strokeOpacity={0.35} />
              <text x={p.x + NODE_W / 2} y={p.y + NODE_H / 2 + 3.5} textAnchor="middle" fontSize={10} fill={c.text}>
                {label.length > 15 ? label.slice(0, 14) + '…' : label}
              </text>
              <title>{`${label} (${n.type})`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default FlowPreview;
